import type { GridSortModel, GridRowSelectionModel, GridSortItem, GridRowId } from "@mui/x-data-grid"
import type { CandidatesModel, CandidateTableFilters } from "@/src/types/candidatesModel"

import { z } from "zod"
import { create } from "zustand"
import { persist } from "zustand/middleware"
import { immer } from "zustand/middleware/immer"
import { useGetLeads } from "@/src/services/leads"
import { CANDIDATES_PAGE_FILTERS_STORAGE_KEY } from "@/src/utils/constants"

import { useAuthStore } from "./auth.slice"

import type { BaseStoreState, PaginationState } from "../types"
import { WritableDraft } from "immer"

// Store state interface
export interface CandidatesStoreState extends BaseStoreState {
    candidates: CandidatesModel[]
    currentCandidate: CandidatesModel | null
    filters: CandidateTableFilters
    searchQuery: string
    pagination: PaginationState
    sortModel: GridSortModel
    selectedRows: GridRowSelectionModel
    openDetails: { leadId?: string; type?: "score" | "details" }
    currentJobId: string | null
    currentRoundId: string | null
    selectedTab: string | undefined
    tabData: { [key: string]: number } | undefined
}

// Store actions interface
export interface CandidatesStoreActions {
    // Candidate management
    fetchCandidates: (jobId?: string, roundId?: string) => Promise<void>
    setCurrentCandidate: (id: string) => void
    moveToNextRound: (candidateId: string) => Promise<void>
    rejectCandidate: (candidateId: string, reason: string) => Promise<void>
    retryCandidate: (candidateId: string) => Promise<void>
    generateOtp: (candidateId: string) => Promise<void>

    // Filters and search
    updateFilters: (filters: Partial<CandidateTableFilters>) => void
    setSearchQuery: (query: string) => void
    setPagination: (pagination: Partial<PaginationState>) => void
    setSortModel: (sortModel: GridSortModel) => void
    setSelectedRows: (rows: GridRowSelectionModel) => void

    // UI state
    toggleDetailsDrawer: (leadId?: string, type?: "score" | "details") => void
    setCurrentJobId: (jobId: string | null) => void
    setCurrentRoundId: (roundId: string | null) => void
    setSelectedTab: (tab: string | undefined) => void

    // Export
    exportCandidates: (candidateIds: string[]) => Promise<void>
}

export type CandidatesStore = CandidatesStoreState & CandidatesStoreActions

// Default filters
const defaultFilters: CandidateTableFilters = {
    createdDate: undefined,
    currentRoundStatus: undefined,
    scheduled: undefined,
}

export const useCandidatesStore = create<CandidatesStore>()(
    persist(
        immer((set, get) => ({
            // State
            candidates: [],
            currentCandidate: null,
            filters: defaultFilters,
            searchQuery: "",
            pagination: { page: 0, pageSize: 20 },
            sortModel: [],
            selectedRows: [],
            openDetails: {},
            currentJobId: null,
            currentRoundId: null,
            selectedTab: undefined,
            tabData: undefined,
            loading: false,
            error: null,

            // Actions
            fetchCandidates: async (jobId?: string, roundId?: string) => {
                set((state) => {
                    state.loading = true
                    state.error = null
                })

                try {
                    const { pagination, sortModel, searchQuery, filters } = get()
                    const { user } = useAuthStore.getState()

                    if (!user?.activeWorkspace?.id) {
                        throw new Error("No active workspace found")
                    }

                    const targetJobId = jobId || get().currentJobId
                    const targetRoundId = roundId || get().currentRoundId

                    if (!targetJobId) {
                        throw new Error("Job ID is required to fetch candidates")
                    }

                    // Use the existing useGetLeads hook logic
                    const { leads, leadsCount, leadsLoading, tabData } = useGetLeads(
                        pagination.page,
                        pagination.pageSize,
                        sortModel,
                        searchQuery,
                        targetJobId,
                        targetRoundId === "Selected" ? undefined : (targetRoundId ?? undefined),
                        {
                            ...filters,
                            currentRoundStatus: targetRoundId === "Selected" ? ["SELECTED"] : undefined,
                        }
                    )

                    set((state) => {
                        state.candidates = leads as CandidatesModel[]
                        // state.tabData = tabData
                        state.loading = leadsLoading
                        state.error = null
                    })
                } catch (error) {
                    set((state) => {
                        state.loading = false
                        state.error = error instanceof Error ? error.message : "Failed to fetch candidates"
                    })
                }
            },

            setCurrentCandidate: (id: string) => {
                const { candidates } = get()
                const candidate = candidates.find((c) => c.id === id)

                set((state) => {
                    state.currentCandidate = candidate || null
                })
            },

            moveToNextRound: async (candidateId: string) => {
                set((state) => {
                    state.loading = true
                    state.error = null
                })

                try {
                    // This would typically call an API to move candidate to next round
                    // For now, we'll update the local state

                    set((state) => {
                        const candidate = state.candidates.find((c) => c.id === candidateId)
                        if (candidate) {
                            // Update candidate status
                            candidate.currentRoundStatus = "PENDING"
                        }
                        state.loading = false
                        state.error = null
                    })
                } catch (error) {
                    set((state) => {
                        state.loading = false
                        state.error = error instanceof Error ? error.message : "Failed to move candidate to next round"
                    })
                    throw error
                }
            },

            rejectCandidate: async (candidateId: string, reason: string) => {
                set((state) => {
                    state.loading = true
                    state.error = null
                })

                try {
                    // This would typically call an API to reject candidate
                    // For now, we'll update the local state

                    set((state) => {
                        const candidate = state.candidates.find((c) => c.id === candidateId)
                        if (candidate) {
                            candidate.currentRoundStatus = "NOT_MATCHED"
                        }
                        state.loading = false
                        state.error = null
                    })
                } catch (error) {
                    set((state) => {
                        state.loading = false
                        state.error = error instanceof Error ? error.message : "Failed to reject candidate"
                    })
                    throw error
                }
            },

            retryCandidate: async (candidateId: string) => {
                set((state) => {
                    state.loading = true
                    state.error = null
                })

                try {
                    // This would typically call an API to retry candidate
                    // For now, we'll update the local state

                    set((state) => {
                        const candidate = state.candidates.find((c) => c.id === candidateId)
                        if (candidate) {
                            candidate.currentRoundStatus = "SCREENING_IN_PROGRESS"
                        }
                        state.loading = false
                        state.error = null
                    })
                } catch (error) {
                    set((state) => {
                        state.loading = false
                        state.error = error instanceof Error ? error.message : "Failed to retry candidate"
                    })
                    throw error
                }
            },

            generateOtp: async (candidateId: string) => {
                set((state) => {
                    state.loading = true
                    state.error = null
                })

                try {
                    // This would typically call an API to generate OTP
                    // For now, we'll just log the action

                    set((state) => {
                        state.loading = false
                        state.error = null
                    })
                } catch (error) {
                    set((state) => {
                        state.loading = false
                        state.error = error instanceof Error ? error.message : "Failed to generate OTP"
                    })
                    throw error
                }
            },

            updateFilters: (filters: Partial<CandidateTableFilters>) => {
                set((state) => {
                    state.filters = { ...state.filters, ...filters }
                    // Reset pagination to first page when filters change to avoid showing empty results
                    // This ensures users always see data after applying filters
                    state.pagination.page = 0
                    state.error = null
                })
            },

            setSearchQuery: (query: string) => {
                set((state) => {
                    state.searchQuery = query
                })
            },

            setPagination: (pagination: Partial<PaginationState>) => {
                set((state) => {
                    state.pagination = { ...state.pagination, ...pagination }
                })
            },

            setSortModel: (sortModel: GridSortModel) => {
                set((state) => {
                    state.sortModel = sortModel as WritableDraft<GridSortItem>[]
                })
            },

            setSelectedRows: (rows: GridRowSelectionModel) => {
                set((state) => {
                    state.selectedRows = rows as WritableDraft<GridRowId>[]
                })
            },

            toggleDetailsDrawer: (leadId?: string, type?: "score" | "details") => {
                set((state) => {
                    state.openDetails = { leadId, type }
                })
            },

            setCurrentJobId: (jobId: string | null) => {
                set((state) => {
                    state.currentJobId = jobId
                })
            },

            setCurrentRoundId: (roundId: string | null) => {
                set((state) => {
                    state.currentRoundId = roundId
                })
            },

            setSelectedTab: (tab: string | undefined) => {
                set((state) => {
                    state.selectedTab = tab
                })
            },

            exportCandidates: async (candidateIds: string[]) => {
                set((state) => {
                    state.loading = true
                    state.error = null
                })

                try {
                    // This would typically call an API to export candidates
                    // For now, we'll just log the action

                    set((state) => {
                        state.loading = false
                        state.error = null
                    })
                } catch (error) {
                    set((state) => {
                        state.loading = false
                        state.error = error instanceof Error ? error.message : "Failed to export candidates"
                    })
                    throw error
                }
            },
        })),
        {
            name: CANDIDATES_PAGE_FILTERS_STORAGE_KEY,
            partialize: (state) => ({
                filters: state.filters,
                searchQuery: state.searchQuery,
                currentJobId: state.currentJobId,
                currentRoundId: state.currentRoundId,
                selectedTab: state.selectedTab,
            }),
        }
    )
)
